import Reveal from "../Reveal";
import { ArrowUpRight } from "lucide-react";
import { useBooking } from "../../lib/booking";

export default function Hero({ settings }) {
  const { openBooking } = useBooking();
  if (!settings) return null;

  return (
    <section
      id="top"
      data-testid="hero"
      style={{
        position: "relative",
        paddingTop: "9rem",
        paddingBottom: "5rem",
        background: "linear-gradient(180deg, var(--fs-bg) 0%, var(--fs-bg) 60%, var(--fs-surface-alt) 100%)",
      }}
    >
      <div className="fs-container" style={{ position: "relative", zIndex: 2 }}>
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <Reveal className="md:col-span-7" data-testid="hero-text">
            {(() => {
              // Split the overline at the first " · " so we can render the second half smaller.
              // Falls back to a single span if no separator is found.
              const raw = settings.hero_overline || "";
              const idx = raw.indexOf("·");
              const main = idx >= 0 ? raw.slice(0, idx).trim() : raw;
              const sub = idx >= 0 ? raw.slice(idx + 1).trim() : "";
              return (
                <p
                  className="fs-overline"
                  data-testid="hero-overline"
                  style={{
                    letterSpacing: "0.16em",
                    lineHeight: 1.2,
                    whiteSpace: "nowrap",
                    margin: 0,
                    display: "flex",
                    alignItems: "baseline",
                    flexWrap: "nowrap",
                    gap: "0.55em",
                  }}
                >
                  <span style={{ fontSize: "clamp(1.4rem, 2.2vw, 1.85rem)" }}>{main}</span>
                  {sub && (
                    <>
                      <span aria-hidden style={{ fontSize: "clamp(1rem, 1.4vw, 1.2rem)", color: "var(--fs-caramel)" }}>·</span>
                      <span style={{ fontSize: "clamp(0.85rem, 1.2vw, 1.05rem)", letterSpacing: "0.22em", opacity: 0.85 }}>
                        {sub}
                      </span>
                    </>
                  )}
                </p>
              );
            })()}

            <h1 className="fs-display-1" style={{ marginTop: "1.5rem", marginBottom: "1.75rem" }} data-testid="hero-title">
              {settings.hero_title_line1}{" "}
              <span className="fs-ital">{settings.hero_title_italic}</span>
              <br />
              {settings.hero_title_line2}
            </h1>

            <p
              style={{
                color: "var(--fs-ink-soft)",
                fontSize: "clamp(1rem, 1.5vw, 1.18rem)",
                lineHeight: 1.7,
                maxWidth: "640px",
                fontWeight: 300,
              }}
              data-testid="hero-subtitle"
            >
              {settings.hero_subtitle}
            </p>

            <div className="flex flex-wrap gap-4" style={{ marginTop: "2.5rem" }}>
              <button
                type="button"
                onClick={openBooking}
                className="fs-btn-primary"
                data-testid="hero-book-cta"
              >
                Reserve a Chair <ArrowUpRight size={14} />
              </button>
              <a href="#services" className="fs-btn-ghost" data-testid="hero-services-cta">
                Explore the Menu
              </a>
            </div>

            <p
              style={{
                marginTop: "1.25rem",
                fontFamily: "'Outfit', sans-serif",
                fontSize: "0.7rem",
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: "var(--fs-muted)",
                fontWeight: 500,
              }}
              data-testid="hero-deposit-note"
            >
              {settings.deposit_note}
            </p>

            <div
              className="grid grid-cols-3 gap-6 md:gap-10"
              style={{
                marginTop: "4rem",
                paddingTop: "2rem",
                borderTop: "1px solid var(--fs-line-strong)",
                maxWidth: "640px",
              }}
              data-testid="hero-stats"
            >
              <Stat num={<><span className="fs-ital">24</span>/7</>} label="Appointments any hour" />
              <Stat num="05" label="Stylists" />
              <Stat num={<>York<span className="fs-ital" style={{ padding: "0 .15em" }}>·</span>PA</>} label="By appointment" />
            </div>
          </Reveal>

          <Reveal className="hidden md:block md:col-span-5" delay={150} data-testid="hero-portrait-wrap">
            <div className="fs-portrait" style={{ aspectRatio: "4/5", maxWidth: "520px", marginLeft: "auto" }}>
              <img src={settings.hero_image_url} alt="Friends Salon 24 — atelier" data-testid="hero-portrait" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Stat({ num, label }) {
  return (
    <div>
      <div
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontWeight: 300,
          fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)",
          color: "var(--fs-caramel)",
          lineHeight: 1,
          marginBottom: "0.5rem",
        }}
      >
        {num}
      </div>
      <div
        style={{
          fontFamily: "'Outfit', sans-serif",
          fontSize: "0.62rem",
          letterSpacing: "0.28em",
          textTransform: "uppercase",
          color: "var(--fs-muted)",
          fontWeight: 600,
        }}
      >
        {label}
      </div>
    </div>
  );
}
